import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Sparkles, Upload, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

const ArtistChannelServicePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    full_name: '',
    biography: ''
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!photoFile) {
      toast.error('Veuillez uploader une photo');
      return;
    }
    
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('full_name', formData.full_name);
      formDataToSend.append('biography', formData.biography);
      formDataToSend.append('photo', photoFile);
      
      await api.post('/service/artist-channel/submit', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
      toast.success('✅ Demande soumise ! Procédez au paiement');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pb-12">
        <Header />
        
        <main className="pt-24 max-w-3xl mx-auto px-4">
          <Card className="glass border-zinc-800 text-center">
            <CardContent className="py-12">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              <h2 className="text-2xl font-bold text-white mb-3">
                Formulaire Validé ! 🎉
              </h2>
              <p className="text-zinc-400 mb-6">
                Procédez au paiement pour créer votre Chaîne Artiste avec Badge Bleu.
              </p>
              <div className="bg-zinc-900 rounded-lg p-6 mb-6">
                <p className="text-3xl font-bold text-[#F97316] mb-3">150$</p>
                <Button className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg">
                  PAYER 150$ MAINTENANT
                </Button>
              </div>
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                Retour au Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <Header />
      
      <main className="pt-24 max-w-3xl mx-auto px-4">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2" size={18} />
          Retour
        </Button>

        <Card className="glass border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Sparkles className="text-blue-500" size={32} />
              Créer Ma Chaîne Artiste - 150$
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Étape 1 : Remplissez ce formulaire. Paiement après validation.
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Nom Complet de l'Artiste *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                  placeholder="Ex: Raoul MLELO"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label>Photo de Profil Professionnelle *</Label>
                <div className="mt-2">
                  {photoPreview && (
                    <div className="mb-4">
                      <img src={photoPreview} alt="Preview" className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-blue-500" />
                    </div>
                  )}
                  <label className="flex items-center justify-center w-full p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-zinc-900">
                    <div className="text-center">
                      <Upload className="mx-auto mb-2 text-zinc-500" size={32} />
                      <p className="text-sm text-zinc-400">
                        {photoFile ? photoFile.name : 'Cliquez pour uploader votre photo'}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                      required
                    />
                  </label>
                </div>
              </div>

              <div>
                <Label>Biographie Artiste *</Label>
                <Textarea
                  value={formData.biography}
                  onChange={(e) => setFormData({...formData, biography: e.target.value})}
                  required
                  placeholder="Décrivez votre parcours musical, vos influences, vos réussites..."
                  rows={6}
                  className="bg-zinc-900 border-zinc-800 text-white resize-none"
                />
                <p className="text-xs text-zinc-500 mt-1">Min. 50 caractères</p>
              </div>

              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4 text-sm text-blue-200">
                <strong>✨ Ce que vous obtenez :</strong>
                <ul className="mt-2 space-y-1 ml-4 list-disc">
                  <li>Badge bleu vérifié sur votre profil</li>
                  <li>Chaîne artiste personnalisée</li>
                  <li>Visibilité augmentée</li>
                  <li>Crédibilité professionnelle</li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:opacity-90 py-6 text-lg font-semibold"
              >
                {submitting ? <Loader2 className="animate-spin" /> : 'CONTINUER VERS LE PAIEMENT'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ArtistChannelServicePage;
